import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CopilotThread } from '../types/database-types';
import { ThreadsService } from '../services/threads/threads.service';
import { useAuth } from '@/contexts/auth';
import { logInfo, logError, LogCategory } from '@/utils/logger';
import { getAllThreadMetadata, getThreadMetadata, getCopilotSidebarState, saveCopilotSidebarState } from '@/utils/localStorage';

export function useThreads() {
  const { user } = useAuth(); // Now uses centralized auth context
  const queryClient = useQueryClient();
  const [localStorageUpdateKey, setLocalStorageUpdateKey] = useState(0);
  
  // ✅ LOCALSTORAGE INTEGRATION: Listen for thread metadata changes AND new thread creation
  useEffect(() => {
    const handleLocalStorageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === 'threadMetadata') {
        // ✅ SMART UPDATES: Only trigger full re-render when thread order changes
        console.log('[useThreads] Thread order changed, triggering re-render:', customEvent.detail.threadId);
        setLocalStorageUpdateKey(prev => prev + 1);
      } else if (customEvent.detail?.key === 'threadMetadataQuiet') {
        // ✅ QUIET UPDATES: Metadata changed but order didn't - just update display without re-render
        console.log('[useThreads] Thread metadata updated quietly:', customEvent.detail.threadId);
        // Don't increment localStorageUpdateKey - this prevents the expensive re-render
        // The individual ThreadItem components will update their display via their own listeners
      } else if (customEvent.detail?.key === 'newThreadCreated') {
        // ✅ NEW THREAD DETECTION: Invalidate cache when new thread is created
        console.log('[useThreads] New thread created, invalidating cache:', customEvent.detail.threadId);
        queryClient.invalidateQueries({ queryKey: ['copilot', 'threads', user?.id] });
        setLocalStorageUpdateKey(prev => prev + 1);
      }
    };

    window.addEventListener('localStorage-change', handleLocalStorageChange);
    return () => window.removeEventListener('localStorage-change', handleLocalStorageChange);
  }, [queryClient, user?.id]);
  
  // Fetch threads for the current user with localStorage-enhanced ordering
  const {
    data: rawThreads = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['copilot', 'threads', user?.id, localStorageUpdateKey], // Include localStorage key for reactivity
    queryFn: async () => {
      const fetchedThreads = await ThreadsService.fetchThreads(user!.id);
      return fetchedThreads;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // ✅ PERFORMANCE OPTIMIZATION: Use useMemo to prevent unnecessary thread array recreation
  // This ensures AnimatePresence sees stable references and doesn't re-animate existing threads
  const threads = useMemo(() => {
    return rawThreads.map(thread => {
      const metadata = getThreadMetadata(thread.id);
      
      return {
        ...thread,
        // ✅ REAL-TIME TITLE: Use localStorage last message if available
        lastMsgFromUser: metadata.lastMessage || thread.lastMsgFromUser || '',
        // ✅ REAL-TIME ACTIVITY: Use localStorage activity for precise ordering
        lastActivity: metadata.lastActivity || new Date(thread.updatedAt || thread.createdAt).getTime(),
        // ✅ MESSAGE COUNT: Add live message count from cache
        messageCount: metadata.messageCount || 0
      };
    }).sort((a, b) => {
      // ✅ PRECISE SORTING: Sort by localStorage activity (most recent first)
      return b.lastActivity - a.lastActivity;
    });
  }, [rawThreads, localStorageUpdateKey]); // Only recompute when rawThreads or metadata actually changes

  // Delete thread mutation with optimistic updates
  const deleteThreadMutation = useMutation({
    mutationFn: (threadId: string) => ThreadsService.deleteThread(threadId),
    onMutate: async (threadId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['copilot', 'threads', user?.id] });

      // Snapshot the previous value
      const previousThreads = queryClient.getQueryData(['copilot', 'threads', user?.id]);

      // Optimistically update to remove the thread
      queryClient.setQueryData(['copilot', 'threads', user?.id], (old: CopilotThread[] = []) => 
        old.filter(thread => thread.id !== threadId)
      );

      // Return a context object with the snapshotted value
      return { previousThreads };
    },
    onError: (err, threadId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['copilot', 'threads', user?.id], context?.previousThreads);
      logError('Failed to delete thread', {
        category: LogCategory.USER_ACTION,
        data: { error: err.message }
      });
    },
    onSuccess: (_, threadId) => {
      logInfo('Thread deleted successfully', { 
        category: LogCategory.USER_ACTION,
        data: { threadId }
      });

      // ✅ DESKTOP EMPTY STATE: Clear active thread if it was the deleted one
      // This ensures users return to the empty state on desktop after deleting the active thread
      const currentState = getCopilotSidebarState();
      if (currentState?.currentThreadId === threadId) {
        console.log('[useThreads] Deleted thread was active, clearing currentThreadId to return to empty state');
        
        saveCopilotSidebarState({
          ...currentState,
          currentThreadId: undefined
        });
        
        // ✅ REACTIVE SYSTEM: Emit change event for synchronization
        window.dispatchEvent(new CustomEvent('localStorage-change', {
          detail: { key: 'currentThreadId', value: undefined }
        }));
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['copilot', 'threads', user?.id] });
    }
  });

  const deleteThread = useCallback((threadId: string) => {
    return deleteThreadMutation.mutateAsync(threadId);
  }, [deleteThreadMutation]);

  return {
    threads,
    isLoading,
    error,
    deleteThread,
    isDeleting: deleteThreadMutation.isPending
  };
}
